/**
 * RettVerk 1.1 - Privacy-First Onboarding Page
 * Første gangs oppsett av lokal lagring og kryptering
 * Sist oppdatert: 20. februar 2026 — versjon 1.1.3
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Shield, HardDrive, Lock, CheckCircle, Loader2, AlertTriangle,
  FolderOpen, Database, Key, Server, ArrowRight, RefreshCw, X, FolderPlus
} from 'lucide-react';
import {
  ONBOARDING_STATUS,
  ONBOARDING_STEPS,
  checkOnboardingStatus,
  runOnboarding,
  checkFileSystemSupport,
  requestLocalFolder,
  createRettVerkFolder
} from '../services/onboardingService';

// Step configuration
const STEP_CONFIG = {
  [ONBOARDING_STEPS.PRIVACY_INTRO]: {
    title: 'Privacy-First',
    description: 'Dine data forblir på din enhet',
    icon: Shield
  },
  [ONBOARDING_STEPS.STORAGE_CHECK]: {
    title: 'Lokal lagring',
    description: 'Sjekker tilgjengelighet',
    icon: HardDrive
  },
  [ONBOARDING_STEPS.ENCRYPTION_SETUP]: {
    title: 'Kryptering',
    description: 'Genererer sikkerhetsnøkler',
    icon: Key
  },
  [ONBOARDING_STEPS.LIBRARY_INIT]: {
    title: 'Bibliotek',
    description: 'Oppretter dokumentbibliotek',
    icon: Database
  },
  [ONBOARDING_STEPS.METADATA_SYNC]: {
    title: 'Synkronisering',
    description: 'Kobler til RettVerk',
    icon: Server
  },
  [ONBOARDING_STEPS.COMPLETE]: {
    title: 'Fullført',
    description: 'Alt er klart',
    icon: CheckCircle
  }
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [phase, setPhase] = useState('intro'); // intro, setup, folder, complete, error
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.PRIVACY_INTRO);
  const [stepStatuses, setStepStatuses] = useState({});
  const [error, setError] = useState(null);
  const [showFolderOption, setShowFolderOption] = useState(false);
  const [folderConnected, setFolderConnected] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check if onboarding is needed
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkOnboardingStatus();
      
      if (status.status === ONBOARDING_STATUS.COMPLETED) {
        // Already completed, go to appropriate dashboard based on account type
        if (user?.account_type === 'business') {
          navigate('/b2b');
        } else {
          navigate('/chat');
        }
      } else if (status.status === ONBOARDING_STATUS.STORAGE_UNAVAILABLE) {
        setPhase('limited');
      }
    };
    
    checkStatus();
  }, [navigate, user]);

  // Handle progress updates
  const handleProgress = useCallback((update) => {
    setStepStatuses(prev => ({
      ...prev,
      [update.step]: update.status
    }));
  }, []);

  // Handle step changes
  const handleStepChange = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  // Start onboarding process
  const startOnboarding = async () => {
    setPhase('setup');
    setIsProcessing(true);
    setError(null);

    try {
      const result = await runOnboarding(
        user?.id,
        token,
        handleProgress,
        handleStepChange
      );

      if (result.success) {
        // Check if File System Access API is supported
        if (checkFileSystemSupport()) {
          setShowFolderOption(true);
          setPhase('folder');
        } else {
          setPhase('complete');
        }
      } else {
        setError(result.error || 'Onboarding feilet');
        setPhase('error');
      }
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
    
    setIsProcessing(false);
  };

  // Automatisk opprett RettVerk 1.10 mappe
  const [createdFolderInfo, setCreatedFolderInfo] = useState(null);
  
  const autoCreateFolder = async () => {
    setIsProcessing(true);
    setFolderError(null);
    
    const result = await createRettVerkFolder();
    
    if (result.success) {
      setFolderConnected(true);
      setCreatedFolderInfo({
        name: result.folderName,
        parent: result.parentFolder
      });
      setFolderError(null);
      setPhase('complete');
    } else if (result.userCancelled) {
      // Brukeren avbrøt - ikke vis feil, la dem prøve igjen
      setFolderError(null);
    } else {
      setFolderError(result.error);
    }
    
    setIsProcessing(false);
  };

  // Connect existing folder (manual selection)
  const connectFolder = async () => {
    setIsProcessing(true);
    setFolderError(null);
    
    const result = await requestLocalFolder();
    
    if (result.success) {
      setFolderConnected(true);
      setCreatedFolderInfo({
        name: result.folderName,
        parent: null
      });
      setFolderError(null);
      setPhase('complete');
    } else if (result.isSystemFolder) {
      // Vis spesifikk feilmelding for systemmappe
      setFolderError(result.error);
    } else if (result.userCancelled) {
      // Brukeren avbrøt - ikke vis feil, la dem prøve igjen
      setFolderError(null);
    } else {
      setFolderError(result.error);
    }
    
    setIsProcessing(false);
  };

  // Skip folder connection
  const skipFolder = () => {
    setPhase('complete');
  };

  // Go to dashboard
  const goToDashboard = () => {
    if (user?.account_type === 'business') {
      navigate('/b2b');
    } else {
      navigate('/chat');
    }
  };

  // Continue with limited functionality
  const continueWithLimited = () => {
    if (user?.account_type === 'business') {
      navigate('/b2b');
    } else {
      navigate('/chat');
    }
  };

  // Retry onboarding
  const retryOnboarding = () => {
    setPhase('intro');
    setStepStatuses({});
    setError(null);
  };

  return (
    <div className="min-h-screen bg-[#0D1520] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        
        {/* Intro Phase */}
        {phase === 'intro' && (
          <div className="animate-fade-in-up">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#C07850] to-[#A8654A] rounded-xl flex items-center justify-center shadow-lg shadow-[#C07850]/20">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-center text-[#F0F4F8] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Privacy-First Oppsett
            </h1>
            <p className="text-[#8899A8] text-center mb-8">
              RettVerk 1.2
            </p>

            {/* Privacy Message */}
            <div className="bg-[#131C2E]/80 border border-emerald-500/20 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-medium text-emerald-400 mb-2">
                    Dine data forblir på din enhet
                  </h3>
                  <p className="text-sm text-[#B8C8D8] leading-relaxed">
                    RettVerk lagrer aldri dokumentene dine eller søkene dine på server.
                    Alt innhold lagres kun på din egen enhet. For å gi deg et personlig 
                    juridisk bibliotek, trenger vi å sette opp lokal lagring nå.
                  </p>
                </div>
              </div>
            </div>

            {/* What we'll do */}
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 text-sm text-[#B8C8D8]">
                <Database className="w-4 h-4 text-[#C07850]" />
                <span>Opprette lokal database for dokumenter</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#B8C8D8]">
                <Key className="w-4 h-4 text-[#C07850]" />
                <span>Generere krypteringsnøkler (lagres kun lokalt)</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#B8C8D8]">
                <HardDrive className="w-4 h-4 text-[#C07850]" />
                <span>Initialisere dokumentbibliotek og søkehistorikk</span>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={startOnboarding}
              className="w-full bg-gradient-to-r from-[#C07850] to-[#A8654A] text-white font-semibold py-3 px-6 rounded-lg hover:from-[#D08960] hover:to-[#B8755A] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#C07850]/20"
            >
              Fortsett
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="text-xs text-[#8899A8] text-center mt-4">
              Dette tar bare noen sekunder
            </p>
          </div>
        )}

        {/* Setup Phase */}
        {phase === 'setup' && (
          <div className="animate-fade-in-up">
            {/* Progress Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-[#131C2E] border border-white/10 rounded-xl flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#C07850] animate-spin" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-[#F0F4F8] mb-6">
              Setter opp Privacy-First...
            </h2>

            {/* Step Progress */}
            <div className="space-y-3">
              {Object.entries(STEP_CONFIG).map(([step, config]) => {
                const status = stepStatuses[step];
                const Icon = config.icon;
                const isActive = currentStep === step;
                const isCompleted = status === 'completed';
                const isFailed = status === 'failed';

                return (
                  <div
                    key={step}
                    className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                      isActive ? 'bg-[#131C2E] border border-[#C07850]/30' :
                      isCompleted ? 'bg-emerald-500/5 border border-emerald-500/20' :
                      isFailed ? 'bg-red-500/5 border border-red-500/20' :
                      'bg-[#131C2E]/50 border border-white/5'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isCompleted ? 'bg-emerald-500/10' :
                      isFailed ? 'bg-red-500/10' :
                      isActive ? 'bg-[#C07850]/10' :
                      'bg-white/5'
                    }`}>
                      {isActive && !isCompleted ? (
                        <Loader2 className="w-5 h-5 text-[#C07850] animate-spin" />
                      ) : isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : isFailed ? (
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      ) : (
                        <Icon className="w-5 h-5 text-[#8899A8]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        isCompleted ? 'text-emerald-400' :
                        isFailed ? 'text-red-400' :
                        isActive ? 'text-[#F0F4F8]' :
                        'text-[#8899A8]'
                      }`}>
                        {config.title}
                      </p>
                      <p className="text-xs text-[#8899A8]">{config.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Folder Selection Phase (Optional) */}
        {phase === 'folder' && (
          <div className="animate-fade-in-up">
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-[#131C2E] border border-emerald-500/30 rounded-xl flex items-center justify-center">
                <FolderOpen className="w-8 h-8 text-emerald-400" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-[#F0F4F8] mb-2">
              Velg lagringssted
            </h2>
            <p className="text-[#8899A8] text-center mb-6 text-sm">
              (Valgfritt, men anbefalt)
            </p>

            <div className="bg-[#131C2E]/80 border border-white/10 rounded-lg p-6 mb-6">
              <p className="text-sm text-[#B8C8D8] leading-relaxed mb-4">
                RettVerk lagrer dokumentene dine kun på din egen enhet. Velg hvordan du vil sette opp lagringsmappen:
              </p>
              
              {/* Mappestruktur-forhåndsvisning */}
              <div className="bg-[#0D1520] rounded-lg p-4 border border-white/5">
                <p className="text-xs text-[#8899A8] mb-2">Følgende mappestruktur opprettes:</p>
                <div className="space-y-1 text-xs text-[#B8C8D8] font-mono">
                  <p>📁 <span className="text-emerald-400">RettVerk 1.2</span>/</p>
                  <p className="pl-4">├── 📁 bibliotek/</p>
                  <p className="pl-4">├── 📁 eksport/</p>
                  <p className="pl-4">└── 📁 temp/</p>
                </div>
              </div>
            </div>

            {/* Feilmelding */}
            {folderError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">
                    {folderError}
                  </p>
                </div>
              </div>
            )}

            {folderConnected && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <p className="text-sm text-emerald-400">
                    Mappen er klar med undermapper for RettVerk!
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {/* Hovedknapp: Automatisk opprettelse */}
              <button
                onClick={autoCreateFolder}
                disabled={isProcessing || folderConnected}
                className="w-full bg-gradient-to-r from-[#C07850] to-[#A8654A] text-white font-semibold py-3 px-6 rounded-lg hover:from-[#D08960] hover:to-[#B8755A] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : folderConnected ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Mappe opprettet
                  </>
                ) : (
                  <>
                    <FolderPlus className="w-5 h-5" />
                    Opprett RettVerk-mappe for meg
                  </>
                )}
              </button>
              
              {/* Sekundærknapp: Velg eksisterende mappe */}
              {!folderConnected && (
                <button
                  onClick={connectFolder}
                  disabled={isProcessing}
                  className="w-full bg-[#131C2E] border border-white/10 text-[#B8C8D8] font-medium py-3 px-6 rounded-lg hover:bg-[#1A2332] hover:border-white/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <FolderOpen className="w-5 h-5" />
                  Jeg har allerede en mappe
                </button>
              )}
              
              {/* Hjelpetekst */}
              <p className="text-xs text-[#8899A8] text-center px-4">
                Velg Dokumenter eller Skrivebord når dialogen åpnes. RettVerk oppretter automatisk en <span className="text-emerald-400 font-medium">RettVerk 1.2</span>-mappe der.
              </p>
              
              <button
                onClick={skipFolder}
                disabled={isProcessing}
                className="w-full text-[#8899A8] hover:text-[#F0F4F8] py-2 transition-colors text-sm"
              >
                Hopp over, bruk kun IndexedDB
              </button>
            </div>
          </div>
        )}

        {/* Complete Phase */}
        {phase === 'complete' && (
          <div className="animate-fade-in-up">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-[#F0F4F8] mb-2">
              Privacy-First aktivert
            </h2>
            <p className="text-emerald-400 text-center mb-8">
              Dokumentene dine lagres kun på denne enheten
            </p>

            {/* Status Summary */}
            <div className="bg-[#131C2E]/80 border border-emerald-500/20 rounded-lg p-5 mb-8">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8899A8]">Lokal lagring</span>
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Aktivert
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8899A8]">Kryptering</span>
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> AES-256-GCM
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8899A8]">Lokal mappe</span>
                  <span className={`flex items-center gap-1 ${folderConnected ? 'text-emerald-400' : 'text-[#8899A8]'}`}>
                    {folderConnected ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {createdFolderInfo?.parent 
                          ? `${createdFolderInfo.parent}/${createdFolderInfo.name}`
                          : createdFolderInfo?.name || 'Tilkoblet'
                        }
                      </>
                    ) : (
                      'Ikke tilkoblet'
                    )}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={goToDashboard}
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-emerald-500 hover:to-emerald-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
            >
              Gå til RettVerk
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Limited Functionality Phase */}
        {phase === 'limited' && (
          <div className="animate-fade-in-up">
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-[#F0F4F8] mb-4">
              Begrenset funksjonalitet
            </h2>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-5 mb-8">
              <p className="text-sm text-[#B8C8D8] leading-relaxed">
                RettVerk kan ikke lagre dokumenter lokalt på denne enheten. 
                Du kan fortsatt bruke tjenesten, men dokumentbibliotek og 
                søkehistorikk vil ikke være tilgjengelig.
              </p>
            </div>

            <button
              onClick={continueWithLimited}
              className="w-full bg-[#131C2E] border border-white/10 text-[#F0F4F8] font-semibold py-3 px-6 rounded-lg hover:bg-[#1A2332] transition-all flex items-center justify-center gap-2"
            >
              Fortsett likevel
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Error Phase */}
        {phase === 'error' && (
          <div className="animate-fade-in-up">
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-center">
                <X className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-[#F0F4F8] mb-4">
              Noe gikk galt
            </h2>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-5 mb-8">
              <p className="text-sm text-[#B8C8D8]">
                {error || 'En ukjent feil oppstod under oppsettet.'}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={retryOnboarding}
                className="w-full bg-gradient-to-r from-[#C07850] to-[#A8654A] text-white font-semibold py-3 px-6 rounded-lg hover:from-[#D08960] hover:to-[#B8755A] transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Prøv igjen
              </button>
              
              <button
                onClick={continueWithLimited}
                className="w-full text-[#8899A8] hover:text-[#F0F4F8] py-2 transition-colors text-sm"
              >
                Fortsett med begrenset funksjonalitet
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#8899A8]/60">
            RettVerk 1.2.0 — Privacy-First arkitektur
          </p>
        </div>
      </div>
    </div>
  );
}
